import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getModulesForCrud, createModule, updateModule, deleteModule } from '../api/modulesApi';
import useModalEffect from '../../../hooks/useModalEffect';

const ModulesManagement = () => {
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  useModalEffect(showModal);
  const [editingModule, setEditingModule] = useState(null);
  const [formData, setFormData] = useState({
    moduleKey: '',
    moduleName: '',
    description: '',
    endpoint: '',
    isActive: true,
    icon: '',
    isMenu: false,
    routePath: '',
    sortOrder: 0
  });

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    setIsLoading(true);
    try {
      const data = await getModulesForCrud();
      setModules(data || []);
    } catch (error) {
      console.error('Error loading modules:', error);
      setModules([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingModule(null);
    setFormData({
      moduleKey: '',
      moduleName: '',
      description: '',
      endpoint: '',
      isActive: true,
      icon: '',
      isMenu: false,
      routePath: '',
      sortOrder: 0
    });
    setShowModal(true);
  };

  const handleEdit = (module) => {
    setEditingModule(module);
    setFormData({
      moduleKey: module.moduleKey,
      moduleName: module.moduleName,
      description: module.description || '',
      endpoint: module.endpoint || '',
      isActive: module.isActive,
      icon: module.icon || '',
      isMenu: module.isMenu || false,
      routePath: module.routePath || '',
      sortOrder: module.sortOrder || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (moduleId) => {
    if (window.confirm('Are you sure you want to delete this module?')) {
      try {
        await deleteModule(moduleId);
        alert('Module deleted successfully!');
        loadModules();
      } catch (error) {
        console.error('Error deleting module:', error);
        alert('Error deleting module: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare data with proper types
      const submitData = {
        ...formData,
        sortOrder: parseInt(formData.sortOrder) || 0
      };
      
      console.log('=== DEBUG: Submit Module Data ===');
      console.log('Original formData:', formData);
      console.log('Processed submitData:', submitData);
      console.log('=== END DEBUG ===');
      
      if (editingModule) {
        await updateModule(editingModule.id, submitData);
        alert('Module updated successfully!');
      } else {
        await createModule(submitData);
        alert('Module created successfully!');
      }
      
      setShowModal(false);
      loadModules();
    } catch (error) {
      console.error('Error saving module:', error);
      alert('Error saving module: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div>
      <div className="row g-2 align-items-center mb-4">
        <div className="col">
          <h4 className="m-0">Manage Modules</h4>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <button
            className="btn btn-primary"
            onClick={handleCreate}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 5l0 14"/>
              <path d="M5 12l14 0"/>
            </svg>
            Add Module
          </button>
        </div>
      </div>

      {/* Modules Table */}
      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-vcenter">
                <thead>
                  <tr>
                    <th>Module Key</th>
                    <th>Module Name</th>
                    <th>Description</th>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th>Menu</th>
                    <th>Sort Order</th>
                    <th style={{ width: '150px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No modules found
                      </td>
                    </tr>
                  ) : (
                    modules.map((module) => (
                      <tr key={module.id}>
                        <td>
                          <code className="text-primary">{module.moduleKey}</code>
                        </td>
                        <td className="fw-bold">{module.moduleName}</td>
                        <td>{module.description || '-'}</td>
                        <td>
                          <code className="text-secondary">{module.endpoint || '-'}</code>
                        </td>
                        <td>
                          <span className={`badge ${module.isActive ? 'bg-green-lt' : 'bg-red-lt'}`}>
                            {module.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${module.isMenu ? 'bg-blue-lt' : 'bg-gray-lt'}`}>
                            {module.isMenu ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>{module.sortOrder || 0}</td>
                        <td>
                          <div className="btn-list">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(module)}
                              title="Edit"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"/>
                                <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"/>
                                <path d="M16 5l3 3"/>
                              </svg>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(module.id)}
                              title="Delete"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M4 7l16 0"/>
                                <path d="M10 11l0 6"/>
                                <path d="M14 11l0 6"/>
                                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"/>
                                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingModule ? 'Edit Module' : 'Add New Module'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Module Key <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="moduleKey"
                          value={formData.moduleKey}
                          onChange={handleInputChange}
                          placeholder="e.g., prospect, leads, proposal"
                          required
                        />
                        <div className="form-text">Unique identifier for the module (lowercase, underscore separated)</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Module Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="moduleName"
                          value={formData.moduleName}
                          onChange={handleInputChange}
                          placeholder="e.g., Prospect, Leads, Proposal"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Describe what this module is for"
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Endpoint</label>
                        <input
                          type="text"
                          className="form-control"
                          name="endpoint"
                          value={formData.endpoint}
                          onChange={handleInputChange}
                          placeholder="e.g., /api/prospects"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Route Path</label>
                        <input
                          type="text"
                          className="form-control"
                          name="routePath"
                          value={formData.routePath}
                          onChange={handleInputChange}
                          placeholder="e.g., /prospects"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Icon</label>
                        <input
                          type="text"
                          className="form-control"
                          name="icon"
                          value={formData.icon}
                          onChange={handleInputChange}
                          placeholder="e.g., ti-user, ti-settings"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Sort Order</label>
                        <input
                          type="number"
                          className="form-control"
                          name="sortOrder"
                          value={formData.sortOrder}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Status</label>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label">Active</label>
                        </div>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="isMenu"
                            checked={formData.isMenu}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label">Show in Menu</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingModule ? 'Update' : 'Create'} Module
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showModal && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
    </div>
  );
};

export default ModulesManagement;
